import React from 'react';
import { Car, Eye, LayoutGrid, List, Pencil, Trash2, Unlink, UserCog, X } from 'lucide-react';
import { Driver } from '../../models/DriverModel';
import DataTable, { DataTableColumn } from '../../components/Base/DataTable';
import StatusBadge from '../../components/Base/StatusBadge';
import DriverCard from './components/DriverCard';
import DriverPassSummaryBadges from './components/DriverPassSummaryBadges';
import './styles/DriversTable.css';

export type DriversViewMode = 'cards' | 'table';

type SortOrder = 'asc' | 'desc';
type DriverSortField = 'first_name' | 'last_name';

type Props = {
  data: Driver[];
  loading?: boolean;
  /** Página completa de edición (ADMIN / GESTOR). */
  onEditPage?: (driver: Driver) => void;
  /** Modal con formulario (SECRETARIA / COORDINADORA). */
  onEditForm?: (driver: Driver) => void;
  onDelete?: (id: number) => void;
  onReleaseVehicle?: (driver: Driver) => void;
  releaseVehicleLoadingIds?: number[];
  showActions?: boolean;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  totalCount?: number;
  searchTerm?: string;
  onClearSearch?: () => void;
  sortOrder?: SortOrder;
  sortField?: DriverSortField;
  sortAnimationKey?: number;
  sortAnimationTarget?: string;
  onSortToggle?: (field: DriverSortField) => void;
  viewMode: DriversViewMode;
  onViewModeChange: (mode: DriversViewMode) => void;
};

const renderSortableHeader = (
  field: DriverSortField,
  label: string,
  sortOrder?: SortOrder,
  sortField?: DriverSortField,
  sortAnimationKey?: number,
  sortAnimationTarget?: string,
  onSortToggle?: (field: DriverSortField) => void
) => {
  if (!onSortToggle) return label;
  const isAnimating = sortAnimationTarget === field;
  const isActive = sortField === field;

  return (
    <button
      key={isAnimating ? `${field}-${sortAnimationKey}` : field}
      type="button"
      className={`table-sort-header${isAnimating ? ' is-sorting' : ''}`}
      onClick={() => onSortToggle(field)}
      aria-label={`Ordenar por ${label}`}
    >
      {label}
      {isActive ? (
        <span className="table-sort-indicator">{sortOrder === 'desc' ? 'Z-A' : 'A-Z'}</span>
      ) : null}
    </button>
  );
};

const DriversTable: React.FC<Props> = ({
  data,
  loading,
  onEditPage,
  onEditForm,
  onDelete,
  onReleaseVehicle,
  releaseVehicleLoadingIds = [],
  showActions = true,
  toolbar,
  footer,
  totalCount,
  searchTerm = '',
  onClearSearch,
  sortOrder,
  sortField,
  sortAnimationKey,
  sortAnimationTarget,
  onSortToggle,
  viewMode,
  onViewModeChange,
}) => {
  const term = searchTerm.trim();
  const shown = data.length;
  const total = totalCount ?? shown;
  const releaseLoadingSet = new Set(releaseVehicleLoadingIds);

  const renderSortButton = (field: DriverSortField, label: string) => {
    if (!onSortToggle) return null;
    const isAnimating = sortAnimationTarget === field;
    const isActive = sortField === field;

    return (
      <button
        key={isAnimating ? `${field}-${sortAnimationKey}` : field}
        type="button"
        className={`drivers-sort-btn${isActive ? ' is-active' : ''}${isAnimating ? ' is-sorting' : ''}`}
        onClick={() => onSortToggle(field)}
        aria-label={`Ordenar por ${label}`}
        aria-pressed={isActive}
      >
        {label}
        {isActive ? (
          <span className="drivers-sort-btn__indicator" aria-hidden>
            {sortOrder === 'desc' ? 'Z–A' : 'A–Z'}
          </span>
        ) : null}
      </button>
    );
  };

  const viewToggle = (
    <div className="drivers-view-toggle" role="group" aria-label="Cambiar vista del listado">
      <button
        type="button"
        className={`drivers-view-toggle__btn${viewMode === 'cards' ? ' is-active' : ''}`}
        onClick={() => onViewModeChange('cards')}
        aria-pressed={viewMode === 'cards'}
        title="Vista de tarjetas"
      >
        <LayoutGrid size={16} strokeWidth={2} aria-hidden />
        <span>Cards</span>
      </button>
      <button
        type="button"
        className={`drivers-view-toggle__btn${viewMode === 'table' ? ' is-active' : ''}`}
        onClick={() => onViewModeChange('table')}
        aria-pressed={viewMode === 'table'}
        title="Vista de tabla"
      >
        <List size={16} strokeWidth={2} aria-hidden />
        <span>Tabla</span>
      </button>
    </div>
  );

  const summary = !loading ? (
    <>
      <span className="data-table__summary-label">
        {term
          ? `${shown} resultado${shown === 1 ? '' : 's'} en esta página`
          : `Mostrando ${shown} de ${total} conductores`}
      </span>
      {term ? (
        <span className="data-table__summary-chip">
          <UserCog size={14} strokeWidth={2.25} aria-hidden />
          <span className="data-table__summary-chip-text">Filtro: “{term}”</span>
          {onClearSearch ? (
            <button
              type="button"
              className="data-table__summary-clear"
              onClick={onClearSearch}
              aria-label="Limpiar filtros de búsqueda"
              title="Limpiar filtro"
            >
              <X size={14} strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}
        </span>
      ) : null}
      {viewMode === 'cards' && onSortToggle ? (
        <div className="drivers-sort-group" role="group" aria-label="Ordenar conductores">
          {renderSortButton('first_name', 'Nombre')}
          {renderSortButton('last_name', 'Apellido')}
        </div>
      ) : null}
      {viewToggle}
    </>
  ) : (
    viewToggle
  );

  const columns: DataTableColumn<Driver>[] = [
    {
      id: 'id',
      header: 'ID',
      width: '88px',
      label: 'ID',
      cell: (row) => <span className="data-table__id">{row.id ?? '—'}</span>,
    },
    {
      id: 'first_name',
      header: renderSortableHeader(
        'first_name',
        'Nombre',
        sortOrder,
        sortField,
        sortAnimationKey,
        sortAnimationTarget,
        onSortToggle
      ),
      label: 'Nombre',
      cell: (row) => (
        <div className="data-table__entity">
          <span className="data-table__entity-avatar drivers-table-avatar" aria-hidden>
            {`${(row.first_name ?? '').charAt(0)}${(row.last_name ?? '').charAt(0)}`.toUpperCase() || '?'}
          </span>
          <span className="data-table__entity-text">
            <span className="data-table__entity-name" title={row.first_name}>
              {row.first_name || '—'}
            </span>
            <span className="data-table__entity-sub">{row.phone?.trim() || 'Sin celular'}</span>
          </span>
        </div>
      ),
    },
    {
      id: 'last_name',
      header: renderSortableHeader(
        'last_name',
        'Apellido',
        sortOrder,
        sortField,
        sortAnimationKey,
        sortAnimationTarget,
        onSortToggle
      ),
      label: 'Apellido',
      cell: (row) => <span className="data-table__primary">{row.last_name || '—'}</span>,
    },
    {
      id: 'address',
      header: 'Dirección',
      label: 'Dirección',
      cell: (row) => (
        <span className="data-table__muted" title={row.address || undefined}>
          {row.address?.trim() || '—'}
        </span>
      ),
    },
    {
      id: 'pass_summary',
      header: 'Pases',
      label: 'Pases',
      cell: (row) => <DriverPassSummaryBadges summary={row.pass_summary} />,
    },
    {
      id: 'vehicle',
      header: 'Vehículo',
      label: 'Vehículo',
      width: '130px',
      cell: (row) =>
        row.vehicle_license_plate ? (
          <span className="drivers-table-vehicle">
            <Car size={14} strokeWidth={2} aria-hidden />
            {row.vehicle_license_plate}
          </span>
        ) : (
          <span className="data-table__muted">Sin vehículo</span>
        ),
    },
    {
      id: 'shift_name',
      header: 'Turno',
      label: 'Turno',
      cell: (row) => <span className="data-table__muted">{row.shift_name ?? '—'}</span>,
    },
    {
      id: 'status',
      header: 'Estado',
      label: 'Estado',
      width: '130px',
      cell: (row) => (
        <StatusBadge
          label={row.status || (row.is_active ? 'Activo' : 'Inactivo')}
          tone={row.status === 'Operativo' || row.is_active ? 'success' : 'neutral'}
        />
      ),
    },
  ];

  if (viewMode === 'table') {
    return (
      <DataTable<Driver>
        className="drivers-data-table"
        columns={columns}
        data={data}
        getRowId={(row) => row.id ?? `${row.first_name}-${row.last_name}`}
        loading={loading}
        emptyMessage={term ? 'Sin conductores para esta búsqueda' : 'Sin conductores'}
        emptyDescription={
          term
            ? 'Prueba con otro filtro o limpia la búsqueda.'
            : 'Crea un nuevo conductor para comenzar.'
        }
        toolbar={toolbar}
        summary={summary}
        footer={footer}
        actionsHeader="Acciones"
        aria-label="Tabla de conductores"
        renderActions={
          showActions && (onEditPage || onEditForm || onDelete || onReleaseVehicle)
            ? (row) => {
                const canRelease = Boolean(
                  row.vehicle_license_plate?.trim() && onReleaseVehicle && row.id != null
                );
                const isReleasing =
                  row.id != null && releaseLoadingSet.has(Number(row.id));
                return (
                  <>
                    {canRelease ? (
                      <button
                        type="button"
                        className="data-table__action data-table__action--release"
                        onClick={() => onReleaseVehicle?.(row)}
                        title="Desasignar vehículo"
                        aria-label={`Desasignar vehículo de ${row.first_name} ${row.last_name}`}
                        disabled={isReleasing}
                      >
                        <Unlink size={16} strokeWidth={2} aria-hidden />
                        <span>{isReleasing ? 'Desasignando…' : 'Desasignar vehículo'}</span>
                      </button>
                    ) : null}
                    {onEditForm ? (
                      <button
                        type="button"
                        className="data-table__action data-table__action--edit"
                        onClick={() => onEditForm(row)}
                        title="Editar conductor"
                        aria-label={`Editar ${row.first_name} ${row.last_name}`}
                      >
                        <Pencil size={16} strokeWidth={2} aria-hidden />
                        <span>Editar</span>
                      </button>
                    ) : null}
                    {onEditPage ? (
                      <button
                        type="button"
                        className="data-table__action data-table__action--detail"
                        onClick={() => onEditPage(row)}
                        title="Ver detalle del conductor"
                        aria-label={`Ver detalle de ${row.first_name} ${row.last_name}`}
                      >
                        <Eye size={16} strokeWidth={2} aria-hidden />
                        <span>Ver detalle</span>
                      </button>
                    ) : null}
                    {onDelete && row.id != null ? (
                      <button
                        type="button"
                        className="data-table__action data-table__action--danger"
                        onClick={() => onDelete(Number(row.id))}
                        title="Eliminar conductor"
                        aria-label={`Eliminar ${row.first_name} ${row.last_name}`}
                      >
                        <Trash2 size={16} strokeWidth={2} aria-hidden />
                        <span>Eliminar</span>
                      </button>
                    ) : null}
                  </>
                );
              }
            : undefined
        }
      />
    );
  }

  return (
    <div className="data-table drivers-data-table">
      <div className="data-table__surface">
        {toolbar ? <div className="data-table__toolbar">{toolbar}</div> : null}
        {summary ? (
          <div className="data-table__summary" role="status" aria-live="polite">
            {summary}
          </div>
        ) : null}

        <div className="drivers-card-grid" aria-label="Listado de conductores en tarjetas">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={`sk-${i}`} className="driver-card-skeleton" aria-hidden>
                <span className="driver-card-skeleton__avatar" />
                <span className="driver-card-skeleton__line" />
                <span className="driver-card-skeleton__line driver-card-skeleton__line--sm" />
                <span className="driver-card-skeleton__line driver-card-skeleton__line--md" />
              </div>
            ))
          ) : data.length === 0 ? (
            <div className="drivers-card-empty">
              <span className="data-table__empty-title">
                {term ? 'Sin conductores para esta búsqueda' : 'Sin conductores'}
              </span>
              <span className="data-table__empty-desc">
                {term
                  ? 'Prueba con otro filtro o limpia la búsqueda.'
                  : 'Crea un nuevo conductor para comenzar.'}
              </span>
            </div>
          ) : (
            data.map((driver) => (
              <DriverCard
                key={driver.id ?? `${driver.first_name}-${driver.last_name}`}
                driver={driver}
                onEditPage={onEditPage}
                onEditForm={onEditForm}
                onDelete={onDelete}
                onReleaseVehicle={onReleaseVehicle}
                releaseVehicleLoading={
                  driver.id != null && releaseLoadingSet.has(Number(driver.id))
                }
                showActions={showActions}
              />
            ))
          )}
        </div>

        {footer ? <div className="data-table__footer">{footer}</div> : null}
      </div>
    </div>
  );
};

export default DriversTable;
